import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { useCallback, useContext, useMemo, useState } from 'react';
import IssuerCorrespondenceItemModal from '@/modules/issuer-dashboard/components/modals/IssuerCorrespondenceItemModal.tsx';
import { useIssuerCommunicationsListQuery } from '@/modules/issuer-dashboard/queries/communications.ts';
import { ModalProviderContext, SearchField, Table, TableBodyColumn, TableHeadColumn } from 'ui';
import {
  CommunicationType,
  CorrespondenceCommunicationActivityItem,
  GetCommunicationCorrespondenceListV1SrCommunicationCorrespondenceTrackingGetParams,
} from 'api';

const headItems: TableHeadColumn[] = [
  { title: 'Subject', className: 'min-w-[250px] !pr-3' },
  { title: 'Sender', className: '!px-3' },
  { title: 'Date & Time', className: 'min-w-[150px] !px-3' },
  { title: 'Type', className: '!px-3' },
  { title: 'Recipients', className: '!px-3 w-40' },
];

type Filters = Omit<
  GetCommunicationCorrespondenceListV1SrCommunicationCorrespondenceTrackingGetParams,
  'page_size' | 'page' | 'portfolio_id' | 'type'
>;

interface PortfolioSourceEmailsTabProps {
  portfolioId: number;
}

export const PortfolioSourceEmailsTab = ({ portfolioId }: PortfolioSourceEmailsTabProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const [filters, setFilters] = useState<Filters>({});
  const { data, isLoading, fetchNextPage, hasNextPage } = useIssuerCommunicationsListQuery({
    page: 1,
    page_size: 15,
    portfolio_id: portfolioId,
    type: CommunicationType.External,
    ...filters,
  });

  const handleOpenCorrespondenceItemModal = useCallback(
    (item: CorrespondenceCommunicationActivityItem) => {
      openModal(IssuerCorrespondenceItemModal, { item });
    },
    [openModal],
  );

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    return (data?.pages || []).flatMap(({ data }) =>
      data.map(item => ({
        onClick: () => handleOpenCorrespondenceItemModal(item),
        cells: [
          { cell: <div className="truncate max-w-[40vw]">{item.subject}</div>, className: '!pr-3' },
          {
            cell: (
              <div className="max-w-[150px]">
                {item.sender_name && <div className="!text-sm truncate">{item.sender_name}</div>}
                {item.sender_email && (
                  <div className="text-gray-600 truncate">{item.sender_email}</div>
                )}
              </div>
            ),
            className: '!px-3',
          },
          { cell: dayjs(item.created_at).format('DD-MM-YYYY, HH:mm'), className: '!px-3' },
          { cell: <div className="capitalize">{item.type}</div>, className: '!px-3' },
          { cell: 'Sent to register', className: '!px-3' },
        ],
      })),
    );
  }, [data?.pages, handleOpenCorrespondenceItemModal]);

  const handleChangeSearchFilter = useCallback((text: string) => {
    setFilters(current => (text ? { ...current, text } : omit(current, 'text')));
  }, []);

  const handleChangeSenderFilter = useCallback((sender: string) => {
    setFilters(current => (sender ? { ...current, sender } : omit(current, 'sender')));
  }, []);

  return (
    <div>
      <div className="px-3 sm:px-6 py-5 grid gap-3 grid-cols-1 md:grid-cols-2">
        <SearchField
          allowClear
          className="max-w-[440px]"
          placeholder="Search by email text"
          onChange={handleChangeSearchFilter}
        />
        <SearchField
          allowClear
          className="max-w-[440px]"
          placeholder="Search by sender name or email"
          onChange={handleChangeSenderFilter}
        />
      </div>
      <Table
        blockIdInfinite="scrollable"
        isSearchUsed={!isEmpty(filters)}
        isLoading={isLoading}
        fetchNextPageInfinite={fetchNextPage}
        hasNextPageInfinite={hasNextPage}
        withRounded={false}
        withBorder={false}
        withBorderX={false}
        className="mt-3 [&_th]:text-xs [&_td:not(.absolute)]:text-xs [&_td.absolute]:!top-[-60px]"
        items={bodyItems}
        headItems={headItems}
      />
    </div>
  );
};
